import { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const { token } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [registration, setRegistration] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 
                      'PushManager' in window && 
                      'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker and check subscription status
  useEffect(() => {
    if (!isSupported || !token) return;

    const initServiceWorker = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        setRegistration(reg);
        
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Service worker registration failed:', error);
      }
    };

    initServiceWorker();
  }, [isSupported, token]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !registration || !token) return false;
    
    setLoading(true);
    try {
      // Request notification permission
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result !== 'granted') {
        setLoading(false);
        return false;
      }

      // Get VAPID public key from backend (or use env var)
      let publicKey = VAPID_PUBLIC_KEY;
      if (!publicKey) {
        const response = await axios.get(`${API}/push/vapid-public-key`);
        publicKey = response.data.publicKey;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      await axios.post(
        `${API}/push/subscribe`,
        {
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Push subscription failed:', error);
      setLoading(false);
      return false;
    }
  }, [isSupported, registration, token]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!registration || !token) return false;
    
    setLoading(true);
    try {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }

      await axios.delete(`${API}/push/unsubscribe`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Push unsubscribe failed:', error);
      setLoading(false);
      return false;
    }
  }, [registration, token]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    if (!token) return false;
    
    try {
      await axios.post(
        `${API}/push/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return true;
    } catch (error) {
      console.error('Test notification failed:', error);
      return false;
    }
  }, [token]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}

export default usePushNotifications;
